import { Component, EventEmitter, Output, inject, Input } from '@angular/core';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RecordingComponent } from '../recording/recording.component';
import { RecordingDialogResult } from '../../models/recording-state.model';

/**
 * Transcription metadata for linking to comments
 */
export interface TranscriptionMetadata {
  transcriptionId: number;
  fileName: string;
}

/**
 * Transcribe component wrapper
 * Opens a recording dialog and emits the transcription result
 *
 * Usage:
 * ```html
 * <xmed-transcribe
 *   [patientId]="patient.id"
 *   [commentId]="comment.id"
 *   (transcriptionResult)="onTranscription($event)"
 *   (transcriptionCreated)="onTranscriptionCreated($event)">
 * </xmed-transcribe>
 * ```
 */
@Component({
  selector: 'xmed-transcribe',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule
],
  template: `
    <div class="w-100 d-flex flex-row align-items-center justify-content-end">
      <button mat-icon-button (click)="openRecorder()">
        <mat-icon>mic</mat-icon>
      </button>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class TranscribeComponent {
  private dialog = inject(MatDialog);

  @Input() patientId?: number;
  @Input() commentId?: number;

  /** Emits the transcription text */
  @Output() transcriptionResult = new EventEmitter<string>();

  /** Emits transcription metadata for linking to comments */
  @Output() transcriptionCreated = new EventEmitter<TranscriptionMetadata>();

  /**
   * Open recording dialog with patient and comment context
   */
  openRecorder(): void {
    const dialogRef = this.dialog.open(RecordingComponent, {
      width: '90vw',
      maxWidth: '800px',
      disableClose: true,
      panelClass: 'recording-confirmation-dialog-panel',
      data: {
        patientId: this.patientId ?? null,
        commentId: this.commentId ?? null
      }
    });

    dialogRef.afterClosed().subscribe((result: RecordingDialogResult | null) => {
      if (result) {
        // Emit the transcription text
        this.transcriptionResult.emit(result.text);

        // Emit transcription metadata if available (for linking to comments)
        if (result.transcriptionId && result.fileName) {
          this.transcriptionCreated.emit({
            transcriptionId: result.transcriptionId,
            fileName: result.fileName
          });
        }
      }
    });
  }
}
